import React, { useEffect, useRef } from 'react';
import { useState } from 'react';
import ThankYou from './ThankYou';
import FeedbackPopUp from './FeedbackPopUp';
import { resetServerContext } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import Reaptcha from 'reaptcha';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import i18n from './i18n';

export default function SubmitPage ({ responses, insights, skipList, setResponses, setSubmit, handleProgressSubmit, currentQuestion, embedded,  embeddedPanelHeight }) {
	const { t } = useTranslation('survey');
	const languagestring = i18n.options.resources[i18n.resolvedLanguage].survey.survey.attributes.langcode;
	const [submitting, setSubmitting] = useState([]);
	const [allowSubmit, setAllowSubmit] = useState('init');
	const [captchaState, setCaptchaState] = useState(false);
	const [feedbackUse, setFeedbackUse] = useState(false);
	const captchaRef = useRef(null);
	let allowEmailSubmit = 'no';

	// pop function to open feedback pop up
	const feedbackPop = () => {
		setFeedbackUse(true);
	};

	/*
	 * Enable or disable the submit button depending on the status of the recaptcha and checkboxes
	 */
	const checkSubmitState = (checkCaptcha = true) => {
		setAllowSubmit('no');
		// If the recaptcha is set, and not verified then it's not submittable
		// Not the extra checkCaptcha is because the `setCaptchaState` doesn't seem to kick in immediately
		if (hasCaptcha && !captchaState && checkCaptcha) {
			return false;
		}
		// If there's a submitting class stop any further calls.
		if (document.querySelector('.survey__footer button').classList.contains('submitting')) {
			return false;
		}
		// If there are any required, but unchecked, agreement inputs it's not submittable
		// Add an error message as well
		let uncheckedRequiredSelector = '.survey__agreements input.required:not(:checked)';
		if (document.querySelector(uncheckedRequiredSelector)) {
			return false;
		}
		// Otherwise allow submitting
		setAllowSubmit('yes');
		return true;
	};

	// If there's a captcha add some vars
	let hasCaptcha = false;
	const onVerify = recaptchaResponse => {
		setCaptchaState(true);
		checkSubmitState(false);
	};
	insights.structure.map((str) => {
		if (str.view === 'submit') {
			if (str.props.captcha) {
				hasCaptcha = true;
				// Recaptcha bits using https://github.com/jsardev/reaptcha
				const verify = () => {
					captchaRef.current.getResponse().then(res => {
						setCaptchaToken(res);
					});
				};
			}
		}
	});

	// Do an initial check to see if this can be submitted.
	// Must be called after rendering the agreements
	if (allowSubmit === 'init') {
		checkSubmitState();
		// This is a bit flakey, but as the agreements aren't renedered yet check the insights for any 'true's
		insights.structure.map((str) => {
			if (str.view === 'submit') {
				str.props.agreements.map((agreement) => {
					if (agreement.required) {
						setAllowSubmit('no');
					}
				});
			}
		});
	}

	// if they skip drag and drop ordered questions
	for (let key in skipList) {
		responses[key] = skipList[key];
	}

	// Get a flat array of keys
	let responseKeys = [];
	Object.keys(responses).forEach((i) => {
		if (Object.keys(responses[i]).length > 1) {
			for (var k = 0; k < Object.keys(responses[i]).length; k++) {
				responseKeys.push([Object.keys(responses[i])[k], Object.values(responses[i])[k]]);
			}
		} else {
			responseKeys.push([Object.keys(responses[i])[0], Object.values(responses[i])[0]]);
		}
	});


	let isEmail = false;
	let emailTo = '';
	let emailBCC = false;
	let emailSubject = '';
	insights.structure.map((str) => {
		if (str.view === 'email') {
			isEmail = true;
			if (str.props.to) {
				emailTo = str.props.to;
				// Check if the email to address is set as a token, if so grab that.
				// Note: this isn't doing anything clever, it relies on the setup in insights being correct.
				if (emailTo.includes('{{')) {
					let emailTos = emailTo.split(';');
					emailTo = '';
					for (var k = 0; k < emailTos.length; k++) {
						if (emailTos[k].includes('{{')) {
							let emailToKey = emailTos[k].replace('{{', '').replace('}}', '');
							for (var i = 0; i < responseKeys.length; i++) {
								if (responseKeys[i][0] === emailToKey) {
									emailTo = emailTo + responseKeys[i][1] + ';';
								}
							}
						}
					}
				}
			}
			if (str.props.bcc) {
				emailBCC = str.props.bcc;
				// Check if the BCC to address is set as a token, if so grab that.
				// Note: this isn't doing anything clever, it relies on the setup in insights being correct.
				if (emailBCC.includes('{{')) {
					let emailBCCs = emailBCC.split(';');
					emailBCC = '';
					for (var k = 0; k < emailBCCs.length; k++) {
						if (emailBCCs[k].includes('{{')) {
							let emailBCCKey = emailBCCs[k].replace('{{', '').replace('}}', '');
							for (var i = 0; i < responseKeys.length; i++) {
								if (responseKeys[i][0] === emailBCCKey) {
									emailBCC = emailBCC + responseKeys[i][1] + ';';
								}
							}
						}
					}
				}
			}
			emailSubject = str.props.subject;
		}
	});

	const stringsToBlockEmail = [
		'[Insert comment here]',
		'[Insert supportive comment here]',
		'[Insert additional supportive comment here]',
		'[Insert your name here]',
		'[Please write your own reason for supporting the application here before pressing send]',
		'[',
		']',
		'[[',
		']]',
		'{{',
		'}}',
	];

	/**
	 * add mark tags to do the text highlighting
	 * [TODO] Move out of function as per linter
	 */
	function applyHighlights (text) {
		// Loop through the strings array and highlight
		for (var i = 0; i < stringsToBlockEmail.length; i++) {
			let string = stringsToBlockEmail[i].replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
			let regex = new RegExp(string, 'g');
			text = text.replace(regex, '<mark>$&</mark>');
		}
		return text;
	}

	/**
	 * Find tokens to replace
	 * [TODO] Move out of function as per linter
	 */
	function replaceTokens (name, value, str) {
		return str.replaceAll('{{' + name + '}}', value);
	}

	const handleSubmit = async (event) => {
		event.preventDefault();
		let uncheckedRequiredSelector = '.survey__agreements input.required:not(:checked)';
		if (document.querySelector(uncheckedRequiredSelector)) {
			let checkID = document.querySelector(uncheckedRequiredSelector).getAttribute('id');
			document.querySelector('.survey__agreements').classList.add('submit-error');
			// Note: this assumes only 1 required option, if there are more validation may be annoying for non-sighted users.
			// But there are announcements of required, and this will just loop until complete.
			document.querySelector('#' + checkID).setAttribute('aria-invalid', true);
			document.querySelector('#' + checkID).setAttribute('aria-describedby', checkID + 'error');
			document.querySelector('#' + checkID).focus();
			return false;
		}

		// Different submits depending on if it's a regular survey, or a supporter portal style email.
		if (isEmail) {
			// Email sending
			let emailError = document.querySelector('#email-error');
			if (stringsToBlockEmail.some(v => document.querySelector('.submit-email-text textarea').value.includes(v))) {
				document.querySelector('.survey__footer').classList.add('submit-error');
				// Scroll to the textarea
				var topPostion = document.querySelector('.submit-email-text textarea').offsetTop;
				document.querySelector('.survey__panel').scrollTop = (topPostion - 160);
				return;
			} else {
				allowEmailSubmit = 'yes';
				document.querySelector('.survey__footer').classList.remove('submit-error');
				let emailBody = document.querySelector('.submit-email-text textarea').value;
				let emailString = 'mailto:' + encodeURIComponent(emailTo);
				emailString = emailString + '?subject=' + encodeURIComponent(emailSubject) + '&body=' + encodeURIComponent(emailBody);
				if (emailBCC) {
					emailString = emailString + '&bcc=' + encodeURIComponent(emailBCC);
				}
				window.location.href = emailString;
			}
		}

		if (allowSubmit !== 'yes') {
			if (allowEmailSubmit !== 'yes') {
				return false;
			}
		}

		// Regular submission or successful emails
		setSubmitting('');

		if (!document.querySelector(uncheckedRequiredSelector)) {
			document.querySelector('.survey__agreements').classList.remove('submit-error');
		}

		const responsesArray = Object.values(responses);
		const responsesObject = responsesArray.reduce(function (result, current) {
			return Object.assign(result, current);
		}, {});

		/*
			Submitting the data
			Using a try/catch, so it will:
			- try to connect to the API, and if it works jump to the thankyou page
			- if it doesn't it will fail and display an error message
			The button also gets a submitting class to (apprear to) be disabled.
		 */
		try {
			setSubmitting(t('survey.submit.submitting'));
			document.querySelector('.survey__footer button').classList.add('submitting');
			document.querySelector('.survey__footer button').classList.remove('error');

			let data = await insights.actions.data(responsesObject);
			if (data) {
				handleShowClick();
			} else {
				throw new TypeError(t('survey.submit.fail'));
			}
		} catch (e) {
			setSubmitting(t('survey.submit.error'));
			document.querySelector('.survey__footer button').classList.remove('submitting');
			document.querySelector('.survey__footer button').classList.add('error');
		}
	};

	// handle agreements
	const handleAgreements = (event) => {
		if (event.target.checked) {
			agreement[event.target.name] = true;
			responses['agreements'] = agreement;
			setResponses({ ...responses });
			if (!document.querySelector('.survey__agreements input.required:not(:checked)')) {
				document.querySelector('.survey__agreements').classList.remove('submit-error');
				if (document.querySelector('.survey__agreements [aria-invalid]')) {
					document.querySelector('.survey__agreements [aria-invalid]').removeAttribute('aria-invalid');
					document.querySelector('.survey__agreements [aria-describedby]').removeAttribute('aria-describedby');
				}
			}
			checkSubmitState();
		} else {
			agreement[event.target.name] = false;
			responses['agreements'] = agreement;
			setResponses({ ...responses });
		}
	};

	// to show thank you page
	const [show, setShow] = useState(false);
	const handleShowClick = () => {
		setShow(true);
	};
	const [agreement, setAgreements] = useState({});

	useEffect(() => {
		// Initial height & highlight on textarea
		if (document.querySelector('.submit-email-text textarea')) {
			heightAndHighlightInit();
			// Also on resize of screen
			window.addEventListener('resize', heightAndHighlightInit, false);
		}
		// set agreements with empty string
		insights.structure.forEach((str) => {
			if (str.view === 'submit') {
				str.props.agreements.forEach((item) => {
					setAgreements(agreement => ({ ...agreement, [`${item.key}`]: false }));
				});
			}
		});
		handleProgressSubmit('Submit');
	}, []);

	const prevQuestionSubmit = () => {
		setSubmit(false);
		const questionNumber = 'Question' + [currentQuestion + 1];
		handleProgressSubmit(questionNumber);
	};

	// Text area resize & highlights background
	// Highlighting based on https://codersblock.com/blog/highlight-text-inside-a-textarea/
	const textareaInput = (e) => {
		// Resize the textarea to contain all the text
		e.target.style.height = '';
		e.target.style.height = e.target.scrollHeight + 6 + 'px'; // Note: the 6px is a small buffer
		// set the text in the highlight
		let text = e.target.value;
		let highlightedText = applyHighlights(text);
		document.querySelector('.submit-email-text .highlights').innerHTML = highlightedText;
	};

	function heightAndHighlightInit () {
		if (document.querySelector('.submit-email-text textarea')) {
			// Height
			document.querySelector('.submit-email-text textarea').style.height = '';
			document.querySelector('.submit-email-text textarea').style.height = document.querySelector('.submit-email-text textarea').scrollHeight + 6 + 'px';
			// Highlight
			let text = document.querySelector('.submit-email-text textarea').value;
			let highlightedText = applyHighlights(text);
			document.querySelector('.submit-email-text .highlights').innerHTML = highlightedText;
		}
	}

	return (
		<>
			{!show && (
				<div className="survey survey__submit">
					{
						insights.structure.map((str) => {
							if (str.view === 'submit') {
								return (
									<div key={str.view}>
										<header className="survey__header">
											<div className="survey__back">
												<button className="plain" onClick={prevQuestionSubmit} >{t('survey.generic.Back')}</button>
											</div>
										</header>
										<main className="survey__panel">
											<div className="survey__review">
												{str.props.logo_url != null ? <div className="survey__logo"><img src={str.props.logo_url} alt="" /></div> : null}
												<h1>{str.props.title}</h1>
												{/* <h2>Optional project subtitle</h2> */}
												<div className="markdown">
													<Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{str.props.statement}</Markdown>
												</div>
												{
													insights.structure.map((str2) => {
														if (str2.view === 'email') {
															let originalText = str2.props.body;
															let replacedText = originalText;
															insights.structure.forEach((obj) => {
																if (obj.view === 'steps') {
																	obj.props['steps'].forEach(
																		(q) => {
																			// Go through each step
																			let isInResponses = responseKeys.find(function (item) {
																				return item[0] === q.key;
																			});
																			if (isInResponses && q.key && q.type !== 'form') {
																				for (var i = 0; i < responseKeys.length; i++) {
																					// If there is a response then replace it
																					if (
																						responseKeys[i][1] &&
																						responseKeys[i][1].length &&
																						responseKeys[i][0] === q.key
																					) {
																						replacedText = replaceTokens(responseKeys[i][0], responseKeys[i][1], replacedText);
																					} else if (
																						q.conditional &&
																						responseKeys[i][0] === q.key &&
																						!responseKeys[i][1].length
																					) {
																						// If there isn't a response but it's conditional then blank it
																						replacedText = replaceTokens(q.key, '', replacedText);
																					}
																				}
																			} else {
																				// If there isn't a response either
																				// - leave it (non conditional)
																				// - or blank it out (is conditional)
																				if (q.type === 'form') {
																					q.fields.forEach((formField) => {
																						let isInResponses = responseKeys.find(function (item) {
																							return item[0] === formField.key;
																						});
																						if (isInResponses && formField.key) {
																							for (var i = 0; i < responseKeys.length; i++) {
																								// If there is a response then replace it
																								if (
																									responseKeys[i][1] &&
																									responseKeys[i][1].length &&
																									responseKeys[i][0] === formField.key
																								) {
																									replacedText = replaceTokens(responseKeys[i][0], responseKeys[i][1], replacedText);
																								}
																							}
																						} else {
																							// If there isn't a response but it's conditional then blank it
																							replacedText = replaceTokens(formField.key, '', replacedText);
																						}
																					});
																				} else if (q.conditional && q.key) {
																					replacedText = replaceTokens(q.key, '', replacedText);
																				}
																			}
																		}
																	);
																}
															});
															return (
																<>
																	<p className="email-subject">{emailSubject}</p>
																	<div className="submit-email-text">
																		<div className="highlights line-breaks"></div>
																		<textarea
																			onChange={(e) => textareaInput(e)}
																			onBlur={(e) => textareaInput(e)}
																			onFocus={(e) => textareaInput(e)}
																		>{replacedText}</textarea>
																	</div>
																</>
															);
														}
													})
												}
											</div>
										</main>
										<footer className="survey__footer survey__footer--submit">
											<form onSubmit={(event) => handleSubmit(event)}>
												<div className='survey__agreements'>
													{
														str.props.agreements.map((agreement) => {
															let agreementID = agreement.key.toLowerCase().replace(/\s/g, '-').replace(/\W/g, '-');
															if (!isEmail) {
																return (
																	<div key={agreement.key}>
																		<div className="survey__agreement-input">
																			<input
																				type={(agreement.required) ? 'radio' : 'checkbox'}
																				id={agreementID}
																				name={agreement.key}
																				className={(agreement.required) ? 'required' : 'not-required'}
																				onClick={(event) => { handleAgreements(event); }}
																			/>
																		</div>
																		<label htmlFor={agreementID} >{agreement.label}&nbsp;{(agreement.required) ? <span className="agreements__required">({t('survey.generic.Required')})</span> : ''}</label>
																		{(agreement.required) ? <div id={agreementID + 'error'} className="agreements__error">{t('survey.submit.must-agree')}</div> : ''}
																	</div>
																);
															}
														})
													}
												</div>
												<div className="survey__recaptcha">
													{(str.props.captcha) ? <Reaptcha sitekey="6LcrWqEqAAAAACnvRtEk-Q1KC9kL0hgk4hFgXsQH" ref={captchaRef} onVerify={onVerify} hl={languagestring} /> : ''}
												</div>
												<div className="survey__policies">
													{str.props.privacy_link != null ? <a href={str.props.privacy_link} target="_blank" rel="noopener noreferrer">{t('survey.phrases.privacy-policy')}</a> : null}
													{str.props.terms_link != null ? <a href={str.props.terms_link} target="_blank" rel="noopener noreferrer">{t('survey.phrases.terms-conditions')}</a> : null}
												</div>
												{(submitting && !isEmail) ? <div id='submit-error' role="alert" aria-atomic="true" className="submit__error"><span></span>{submitting}</div> : ''}
												{
													insights.structure.map((str2) => {
														if (str2.view === 'email') {
															return (
																<div id="email-error" className="email__error">{t('survey.submit.email-edit')}</div>
															);
														}
													})
												}
												{!feedbackUse && (<button className="open_popup plain" onClick={feedbackPop}>{t('survey.intro.how')}</button>)}
												{feedbackUse && (<FeedbackPopUp setFeedbackUse={setFeedbackUse} clientName={insights.structure[0].props.client_name} clientEmail={insights.structure[0].props.client_email} clientPhone={insights.structure[0].props.client_phone} clientPrivacy={insights.structure[0].props.client_privacy} setShow={setShow}/>)}
												
												<button type="submit" className={isEmail ? 'yes' : allowSubmit}>{isEmail ? t('survey.phrases.send-email') : t('survey.generic.Submit')}</button>
											</form>
										</footer>
									</div>
								);
							}
						})
					}
				</div>
			)}
			{show && (<ThankYou insights={insights} handleProgressSubmit={handleProgressSubmit} embedded={embedded} embeddedPanelHeight={embeddedPanelHeight}/>)}
		</>
	);
}
